// Zustand store for checkout state management

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CustomShippingOption, ThemeConfig, CheckoutCompleteResult } from '../types/config';

// =====================================================
// TYPES
// =====================================================

export type CheckoutStep = 'identity' | 'address' | 'shipping' | 'payment';

export interface CustomerData {
  email: string;
  name: string;
  phone: string;
}

export interface Address {
  id?: string;
  streetAddress: string;
  postalCode: string;
  city: string;
  countryCode: string;
  isDefault?: boolean;
}

export interface ShippingOption {
  id: string;
  provider: string;
  method: string;
  displayName: string;
  description: string;
  price: { amount: number; currency: string };
  deliveryDays: number;
  estimatedDelivery?: { date: string; display: string };
  isPickup: boolean;
  isCheapest?: boolean;
  isFastest?: boolean;
  logoUrl?: string;
}

export interface PaymentMethod {
  type: string;
  displayName: string;
  iconUrl: string;
  isAvailable: boolean;
  isLastUsed?: boolean;
  isRecommended?: boolean;
  savedCard?: {
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

export interface CartItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: { amount: number; currency: string };
  imageUrl?: string;
}

export interface CartData {
  items: CartItem[];
  totalAmount: { amount: number; currency: string };
  totalItems: number;
}

export interface MerchantConfig {
  merchantId: string;
  merchantName: string;
  logoUrl?: string;
  primaryColor?: string;
  features: {
    enableQuickPay: boolean;
    enableAddressSaving: boolean;
    enableVipps: boolean;
    collectPhone: boolean;
  };
  storeInfo?: {
    storeId: string;
    name: string;
    email?: string;
    phone?: string;
    merchantWebshopURL?: string;
    termsAndConditionsURL?: string;
    privacyPolicyURL?: string;
  };
  // Activated payment methods from Surfboard API
  activatedPaymentMethods?: string[];
}

// =====================================================
// CHECKOUT RESULT
// =====================================================

export interface PaymentResult {
  success: boolean;
  paymentId: string;
  orderId: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED' | 'CANCELLED';
  paymentMethod: string;
  errorMessage?: string;
  card?: {
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

// =====================================================
// STORE STATE
// =====================================================

interface CheckoutState {
  // Session
  sessionId: string | null;
  surfboardOrderId: string | null;
  surfboardNonce: string | null;
  publicKey: string | null;
  
  // Steps
  currentStep: CheckoutStep;
  completedSteps: Set<CheckoutStep>;
  
  // Data
  customer: CustomerData | null;
  shippingAddress: Address | null;
  billingAddress: Address | null;
  useSameAddress: boolean;
  selectedShipping: ShippingOption | null;
  selectedPaymentMethod: PaymentMethod | null;
  
  // Available options
  shippingOptions: ShippingOption[];
  paymentMethods: PaymentMethod[];
  savedAddresses: Address[];
  
  // Custom shipping (from config)
  customShippingOptions: CustomShippingOption[];
  useBuiltInShipping: boolean;
  skipShipping: boolean;
  
  // Theming
  theme: ThemeConfig | null;
  
  // Cart
  cart: CartData | null;
  
  // Merchant
  merchant: MerchantConfig | null;
  
  // UI state
  isLoading: boolean;
  error: string | null;
  
  // Quick Pay
  hasQuickPay: boolean;
  deviceToken: string | null;
  
  // Preferences
  lastUsedPaymentMethod: string | null;
  
  // Payment result (for callback)
  paymentResult: PaymentResult | null;
}

interface CheckoutActions {
  // Session
  initSession: (sessionId: string, data: Partial<CheckoutState>) => void;
  setSurfboardData: (orderId: string, nonce: string, publicKey: string) => void;
  
  // Navigation
  setStep: (step: CheckoutStep) => void;
  nextStep: () => void;
  prevStep: () => void;
  completeStep: (step: CheckoutStep) => void;
  
  // Customer
  setCustomer: (customer: CustomerData) => void;
  
  // Address
  setShippingAddress: (address: Address) => void;
  setBillingAddress: (address: Address) => void;
  setUseSameAddress: (same: boolean) => void;
  setSavedAddresses: (addresses: Address[]) => void;
  
  // Shipping
  setShippingOptions: (options: ShippingOption[]) => void;
  selectShipping: (option: ShippingOption) => void;
  setCustomShippingOptions: (options: CustomShippingOption[]) => void;
  setUseBuiltInShipping: (use: boolean) => void;
  setSkipShipping: (skip: boolean) => void;
  
  // Payment
  setPaymentMethods: (methods: PaymentMethod[]) => void;
  selectPaymentMethod: (method: PaymentMethod) => void;
  setPaymentResult: (result: PaymentResult) => void;
  
  // Theming
  setTheme: (theme: ThemeConfig) => void;
  
  // Cart
  setCart: (cart: CartData) => void;
  
  // Merchant
  setMerchant: (merchant: MerchantConfig) => void;
  
  // UI
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Quick Pay
  setDeviceToken: (token: string | null) => void;
  setHasQuickPay: (has: boolean) => void;
  
  // Checkout complete - notify parent
  completeCheckout: (result: PaymentResult) => void;
  
  // Reset
  reset: () => void;
}

// =====================================================
// INITIAL STATE
// =====================================================

const initialState: CheckoutState = {
  sessionId: null,
  surfboardOrderId: null,
  surfboardNonce: null,
  publicKey: null,
  
  currentStep: 'identity',
  completedSteps: new Set(),
  
  customer: null,
  shippingAddress: null,
  billingAddress: null,
  useSameAddress: true,
  selectedShipping: null,
  selectedPaymentMethod: null,
  
  shippingOptions: [],
  paymentMethods: [],
  savedAddresses: [],
  
  // Custom shipping defaults
  customShippingOptions: [],
  useBuiltInShipping: true,
  skipShipping: false,
  
  // Theme
  theme: null,
  
  cart: null,
  merchant: null,
  
  isLoading: false,
  error: null,
  
  hasQuickPay: false,
  deviceToken: null,
  lastUsedPaymentMethod: null,
  
  paymentResult: null,
};

// =====================================================
// STEP ORDER (removed 'complete' - we just return data)
// =====================================================

const STEP_ORDER: CheckoutStep[] = ['identity', 'address', 'shipping', 'payment'];

// =====================================================
// POST MESSAGE HELPER
// =====================================================

function notifyParent(type: string, data: unknown) {
  // Send to parent window (for iframe)
  if (window.parent !== window) {
    window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, '*');
  }
  
  // Also dispatch custom event (for inline widget)
  window.dispatchEvent(new CustomEvent(`sagapay:${type.toLowerCase()}`, { detail: data }));
  
  // Call global callback if defined
  const config = window.SagaPayCheckoutConfig;
  if (config) {
    switch (type) {
      case 'CHECKOUT_COMPLETE':
        config.onComplete?.(data as any);
        break;
      case 'CHECKOUT_ERROR':
        config.onError?.(data as any);
        break;
      case 'CHECKOUT_CANCELLED':
        config.onCancel?.();
        break;
      case 'STEP_CHANGED':
        config.onStepChange?.((data as any).step);
        break;
    }
  }
}

// =====================================================
// STORE
// =====================================================

export const useCheckoutStore = create<CheckoutState & CheckoutActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        
        // Session
        initSession: (sessionId, data) => {
          set({
            sessionId,
            ...data,
            completedSteps: new Set(),
          });
        },
        
        setSurfboardData: (orderId, nonce, publicKey) => {
          set({
            surfboardOrderId: orderId,
            surfboardNonce: nonce,
            publicKey,
          });
        },
        
        // Navigation
        setStep: (step) => set({ currentStep: step }),
        
        nextStep: () => {
          const { currentStep } = get();
          const currentIndex = STEP_ORDER.indexOf(currentStep);
          if (currentIndex < STEP_ORDER.length - 1) {
            set({ currentStep: STEP_ORDER[currentIndex + 1] });
          }
        },
        
        prevStep: () => {
          const { currentStep } = get();
          const currentIndex = STEP_ORDER.indexOf(currentStep);
          if (currentIndex > 0) {
            set({ currentStep: STEP_ORDER[currentIndex - 1] });
          }
        },
        
        completeStep: (step) => {
          const { completedSteps } = get();
          const newCompleted = new Set(completedSteps);
          newCompleted.add(step);
          set({ completedSteps: newCompleted });
        },
        
        // Customer
        setCustomer: (customer) => set({ customer }),
        
        // Address
        setShippingAddress: (address) => set({ shippingAddress: address }),
        setBillingAddress: (address) => set({ billingAddress: address }),
        setUseSameAddress: (same) => set({ useSameAddress: same }),
        setSavedAddresses: (addresses) => set({ savedAddresses: addresses }),
        
        // Shipping
        setShippingOptions: (options) => set({ shippingOptions: options }),
        selectShipping: (option) => set({ selectedShipping: option }),
        setCustomShippingOptions: (options) => set({ customShippingOptions: options }),
        setUseBuiltInShipping: (use) => set({ useBuiltInShipping: use }),
        setSkipShipping: (skip) => set({ skipShipping: skip }),
        
        // Payment
        setPaymentMethods: (methods) => set({ paymentMethods: methods }),
        selectPaymentMethod: (method) => set({ selectedPaymentMethod: method }),
        setPaymentResult: (result) => set({ paymentResult: result }),
        
        // Theming
        setTheme: (theme) => set({ theme }),
        
        // Cart
        setCart: (cart) => set({ cart }),
        
        // Merchant
        setMerchant: (merchant) => set({ merchant }),
        
        // UI
        setLoading: (loading) => set({ isLoading: loading }),
        setError: (error) => set({ error }),
        
        // Quick Pay
        setDeviceToken: (token) => set({ deviceToken: token }),
        setHasQuickPay: (has) => set({ hasQuickPay: has }),
        
        // Complete checkout - notify parent with result
        completeCheckout: (result) => {
          const state = get();
          
          // Build complete result
          const completeResult: CheckoutCompleteResult = {
            success: result.success,
            paymentId: result.paymentId,
            orderId: result.orderId,
            sessionId: state.sessionId || '',
            status: result.status,
            paymentMethod: result.paymentMethod,
            amount: {
              total: (state.cart?.totalAmount.amount || 0) + (state.selectedShipping?.price.amount || 0),
              subtotal: state.cart?.totalAmount.amount || 0,
              shipping: state.selectedShipping?.price.amount || 0,
              currency: state.cart?.totalAmount.currency || 'NOK',
            },
            customer: {
              email: state.customer?.email || '',
              name: state.customer?.name || '',
              phone: state.customer?.phone,
            },
            shippingAddress: {
              streetAddress: state.shippingAddress?.streetAddress || '',
              postalCode: state.shippingAddress?.postalCode || '',
              city: state.shippingAddress?.city || '',
              countryCode: state.shippingAddress?.countryCode || 'NO',
            },
            shipping: {
              id: state.selectedShipping?.id || '',
              name: state.selectedShipping?.displayName || '',
              provider: state.selectedShipping?.provider || '',
              price: state.selectedShipping?.price.amount || 0,
              estimatedDelivery: state.selectedShipping?.estimatedDelivery?.display || '',
            },
            completedAt: new Date().toISOString(),
            errorMessage: result.errorMessage,
            card: result.card,
          };
          
          set({ paymentResult: result });
          
          // Notify parent/callbacks
          if (result.success) {
            notifyParent('CHECKOUT_COMPLETE', completeResult);
          } else {
            notifyParent('CHECKOUT_ERROR', {
              code: result.status,
              message: result.errorMessage || 'Payment failed',
            });
          }
        },
        
        // Reset
        reset: () => set(initialState),
      }),
      {
        name: 'sagapay-checkout',
        partialize: (state) => ({
          // Only persist these fields
          deviceToken: state.deviceToken,
        }),
      }
    ),
    { name: 'CheckoutStore' }
  )
);

// =====================================================
// SELECTORS
// =====================================================

export const useCurrentStep = () => useCheckoutStore((s) => s.currentStep);
export const useIsLoading = () => useCheckoutStore((s) => s.isLoading);
export const useError = () => useCheckoutStore((s) => s.error);
export const useCart = () => useCheckoutStore((s) => s.cart);
export const useMerchant = () => useCheckoutStore((s) => s.merchant);
export const useCustomer = () => useCheckoutStore((s) => s.customer);
export const useShippingAddress = () => useCheckoutStore((s) => s.shippingAddress);
export const useSelectedShipping = () => useCheckoutStore((s) => s.selectedShipping);
export const usePaymentMethods = () => useCheckoutStore((s) => s.paymentMethods);

// Computed: Total with shipping
export const useTotalWithShipping = () => {
  const cart = useCheckoutStore((s) => s.cart);
  const shipping = useCheckoutStore((s) => s.selectedShipping);
  
  if (!cart) return null;
  
  const shippingAmount = shipping?.price.amount || 0;
  return {
    amount: cart.totalAmount.amount + shippingAmount,
    currency: cart.totalAmount.currency,
  };
};

// Computed: Progress percentage
export const useProgressPercentage = () => {
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const stepIndex = STEP_ORDER.indexOf(currentStep);
  return Math.round(((stepIndex + 1) / STEP_ORDER.length) * 100);
};

// Computed: Can proceed to next step
export const useCanProceed = () => {
  const state = useCheckoutStore();
  
  switch (state.currentStep) {
    case 'identity':
      return !!state.customer?.email && !!state.customer?.name;
    case 'address':
      return !!state.shippingAddress?.streetAddress && !!state.shippingAddress?.postalCode;
    case 'shipping':
      return !!state.selectedShipping;
    case 'payment':
      return !!state.selectedPaymentMethod;
    default:
      return false;
  }
};
